// Report shaping for the chat widget's "Reports & Analysis" branch.
//
// PORTED FROM: ipoultry-mobile src/Report/Report.js (normalizeReportResponse)
// and src/Report/components/reportHelpers.js. The Django service wraps the
// analysis in several envelope shapes depending on the path taken, so the
// normalizer accepts them all and returns one flat report object the card can
// render.

const isPlainObject = (v) => v && typeof v === "object" && !Array.isArray(v);

const safeArray = (v) => (Array.isArray(v) ? v : []);

const pick = (response, reportData, key, fallback = null) =>
  response?.[key] ?? response?.data?.[key] ?? reportData?.[key] ?? fallback;

// Accepts the raw generate-report / phase-wise response and returns the flat
// report object, or null when there is nothing renderable in it.
export const normalizeReportResponse = (response, { reportType, farmId, batchId } = {}) => {
  if (!isPlainObject(response)) return null;

  const reportData = isPlainObject(response?.analysis?.data)
    ? response.analysis.data
    : isPlainObject(response?.data?.analysis?.data)
    ? response.data.analysis.data
    : isPlainObject(response?.data)
    ? response.data
    : isPlainObject(response?.analysis)
    ? response.analysis
    : response;

  if (!isPlainObject(reportData)) return null;

  let score = Number(reportData.production_score);
  if (Number.isNaN(score)) score = 0;

  return {
    ...reportData,
    status: reportData.status ?? "Good",
    production_score: score,
    heading: reportData.heading ?? "Farm Report",
    summary: reportData.summary ?? "",
    warnings: safeArray(reportData.warnings),
    key_findings: safeArray(reportData.key_findings),
    recommended_actions: safeArray(
      Array.isArray(reportData.recommended_actions)
        ? reportData.recommended_actions
        : reportData.recommendations
    ),
    data_quality: safeArray(reportData.data_quality),
    report_type: pick(response, reportData, "report_type", reportType ?? null),
    farm_id: pick(response, reportData, "farm_id", farmId ?? null),
    batch_id: pick(response, reportData, "batch_id", batchId ?? null),
    analysis_date: pick(response, reportData, "analysis_date"),
    batch_name: pick(response, reportData, "batch_name"),
    start_date: pick(response, reportData, "start_date"),
    end_date: pick(response, reportData, "end_date"),
    current_day: pick(response, reportData, "current_day"),
    farm_name: pick(response, reportData, "farm_name"),
    breed_name: pick(response, reportData, "breed_name"),
  };
};

// ---- text helpers -------------------------------------------------------

const objectToText = (value) => {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (Array.isArray(value)) return value.map(objectToText).filter(Boolean).join(", ");
  if (typeof value === "object") {
    return Object.entries(value)
      .map(([key, item]) => {
        const label = key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
        const text = objectToText(item);
        return text ? `${label}: ${text}` : "";
      })
      .filter(Boolean)
      .join("\n");
  }
  return "";
};

// LLM output may carry markdown noise; strip it down to plain sentences.
export const cleanText = (value) =>
  objectToText(value)
    .replace(/\*\*|__|`/g, "")
    .replace(/#{1,6}\s*/g, "")
    .replace(/\r?\n+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

// ---- item accessors (warnings / findings / actions are loosely shaped) --

export const getItemHeading = (item, index) => {
  if (typeof item === "string") return `Item ${index + 1}`;
  return (
    item?.heading ??
    item?.title ??
    item?.name ??
    item?.parameter ??
    item?.metric ??
    item?.issue ??
    item?.finding ??
    `Item ${index + 1}`
  );
};

export const getItemBody = (item) => {
  if (typeof item === "string") return item;
  return (
    item?.action ??
    item?.description ??
    item?.recommendation ??
    item?.recommended_action ??
    item?.details ??
    item?.message ??
    item?.text ??
    ""
  );
};

export const getItemPriority = (item) => {
  if (!item || typeof item !== "object") return "Normal";
  return item.priority ?? item.Priority ?? item.severity ?? item.level ?? item.status ?? "Normal";
};

export const normalizePriority = (priority) => {
  const v = String(priority ?? "Normal").toLowerCase();
  if (v.includes("critical") || v.includes("urgent")) return "critical";
  if (v.includes("high") || v.includes("warning")) return "high";
  if (v.includes("medium") || v.includes("moderate")) return "medium";
  if (v.includes("low")) return "low";
  return "normal";
};

// ---- status / score palettes (same values as the mobile card) -----------

export const normalizeStatus = (status) => {
  const v = String(status ?? "Good").toLowerCase();
  if (v.includes("critical")) return "critical";
  if (v.includes("warning")) return "warning";
  if (v.includes("upcoming")) return "upcoming";
  return "good";
};

export const getStatusColors = (status) => {
  switch (normalizeStatus(status)) {
    case "critical":
      return { background: "#FEE2E2", border: "#FECACA", text: "#B91C1C" };
    case "warning":
      return { background: "#FEF3C7", border: "#FDE68A", text: "#B45309" };
    case "upcoming":
      return { background: "#F3F4F6", border: "#E5E7EB", text: "#6B7280" };
    default:
      return { background: "#DCFCE7", border: "#BBF7D0", text: "#15803D" };
  }
};

export const normalizeScore = (score) => {
  const n = Number(score);
  if (Number.isNaN(n)) return 0;
  return Math.max(0, Math.min(100, n));
};

export const getScoreLabel = (score) => {
  const n = normalizeScore(score);
  if (n >= 85) return "Excellent";
  if (n >= 70) return "Good";
  if (n >= 50) return "Needs Attention";
  return "Critical";
};

export const getScoreColor = (score) => {
  const n = normalizeScore(score);
  if (n >= 70) return "#16A34A";
  if (n >= 50) return "#D97706";
  return "#DC2626";
};

export const getProductionJourney = (report) => safeArray(report?.production_journey);

export const getFindings = (report) => safeArray(report?.good_points ?? report?.key_findings);

export const isPhaseWiseReport = (report) =>
  getProductionJourney(report).length > 0 ||
  String(report?.report_type || "").toLowerCase().includes("phase");
